import { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { List, RowComponentProps, useDynamicRowHeight } from "react-window";

import { Button } from "../button";
import styles from "./style.module.scss";

import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

interface PdfViewerProps {
	file: File | string;
	allPages?: boolean;
	onLoadSuccess?: () => void;
	onRenderSuccess?: () => void;
	onScroll?: (element: HTMLElement) => void;
}

interface PdfPageRowProps {
	width: number;
	onRenderSuccess?: () => void;
}

const VIRTUALIZATION_THRESHOLD = 10;

const options = {
	cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
	wasmUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/wasm/`,
	standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
};

export function PdfViewer({ file, allPages, onLoadSuccess, onRenderSuccess, onScroll }: PdfViewerProps) {
	const [pages, setPages] = useState(0);
	const [pageNumber, setPageNumber] = useState(1);
	const viewerRef = useRef<HTMLDivElement>(null);
	const [width, setWidth] = useState<number | undefined>(undefined);

	useEffect(() => {
		pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
	}, []);

	useEffect(() => {
		const viewer = viewerRef.current;
		if (!viewer) return;

		const updateWidth = () => setWidth(viewer.clientWidth);
		updateWidth();

		const observer = new ResizeObserver(updateWidth);
		observer.observe(viewer);
		return () => observer.disconnect();
	}, []);

	function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
		setPages(numPages);
		onLoadSuccess?.();
	}

	if (allPages) {
		return (
			<Document
				options={options}
				className="pdf-viewer"
				file={file}
				inputRef={viewerRef}
				onLoadSuccess={onDocumentLoadSuccess}
			>
				{width > 0 && pages > VIRTUALIZATION_THRESHOLD ? (
					<VirtualizedPages
						pages={pages}
						width={width}
						onRenderSuccess={onRenderSuccess}
						onScroll={onScroll}
					/>
				) : (
					Array.from({ length: pages }, (_, i) => (
						<Page key={i} pageNumber={i + 1} width={width} onRenderSuccess={onRenderSuccess} />
					))
				)}
			</Document>
		);
	}

	return (
		<>
			<Document
				options={options}
				className="pdf-viewer"
				file={file}
				inputRef={viewerRef}
				onLoadSuccess={onDocumentLoadSuccess}
			>
				<Page pageNumber={pageNumber} width={width} onRenderSuccess={onRenderSuccess} />
			</Document>
			{pages > 1 ? (
				<div className={styles.pagination}>
					<Button onClick={() => setPageNumber(pageNumber - 1)} disabled={pageNumber === 1}>
						Previous
					</Button>
					<Button onClick={() => setPageNumber(pageNumber + 1)} disabled={pageNumber === pages}>
						Next
					</Button>
					<p>
						Page {pageNumber} of {pages}
					</p>
				</div>
			) : null}
		</>
	);
}

interface VirtualizedPagesProps {
	pages: number;
	width: number;
	onRenderSuccess?: () => void;
	onScroll?: (element: HTMLElement) => void;
}

function VirtualizedPages({ pages, width, onRenderSuccess, onScroll }: VirtualizedPagesProps) {
	const rowHeight = useDynamicRowHeight({ defaultRowHeight: width * 1.5 });

	return (
		<List
			defaultHeight={600}
			rowCount={pages}
			rowHeight={rowHeight}
			rowProps={{ width, onRenderSuccess }}
			rowComponent={PdfPageRow}
			onScroll={(event) => onScroll?.(event.currentTarget)}
			overscanCount={2}
			style={{ height: "100%", width: "100%" }}
		/>
	);
}

function PdfPageRow({ index, style, width, onRenderSuccess }: RowComponentProps<PdfPageRowProps>) {
	return (
		<div style={style}>
			<Page pageNumber={index + 1} width={width} onRenderSuccess={onRenderSuccess} />
		</div>
	);
}
